import { useState, useCallback, useMemo, useEffect } from 'react'
import { useFFEStore } from './../../store/ffeStore'
import type { SearchResult } from './types'
import { useScrollToElement } from '../FloorPlans/hooks/useScrollToElement'

export const useGoBar = (searchTerm: string) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const scrollToElement = useScrollToElement()

  // Filter and prepare search results
  const results = useMemo(() => {
    if (!searchTerm) return []

    const term = searchTerm.toLowerCase()
    const results: SearchResult[] = []

    const state = useFFEStore.getState()
    const groups = state.document.groups
    const lineItems = state.document.lineItems

    // Search groups
    groups.forEach((group) => {
      if (group.name.toLowerCase().includes(term)) {
        results.push({
          type: 'group',
          id: group.id,
          name: group.name
        })
      }
    })

    // Search line items
    lineItems.forEach((item) => {
      const matchesName = item.name?.toLowerCase().includes(term)
      const matchesCode = item.code?.toLowerCase().includes(term)

      if (matchesName || matchesCode) {
        const group = item.groupId
          ? groups.find((g) => g.id === item.groupId)
          : undefined

        results.push({
          type: 'item',
          id: item.id,
          name: item.name,
          code: item.code,
          groupId: item.groupId,
          groupName: group?.name
        })
      }
    })

    return results
  }, [searchTerm])

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [results.length])

  // Handle keyboard navigation
  const handleKeyNavigation = useCallback(
    (e: React.KeyboardEvent, onClose: () => void) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((i) => Math.min(i + 1, results.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((i) => Math.max(i - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          if (results[selectedIndex]) {
            handleSelect(results[selectedIndex])
            onClose()
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    },
    [results, selectedIndex]
  )

  // Handle result selection
  const handleSelect = useCallback(
    (result: SearchResult) => {
      const elementId =
        result.type === 'item' ? `line-item-${result.id}` : `group-${result.id}`
      if(document.getElementById(elementId)) {
        scrollToElement(elementId)
      } else {
        useFFEStore.getState().expandAllGroups()
        setTimeout(() => {
          scrollToElement(elementId)
        }, 100)
      }
    },
    [scrollToElement]
  )

  return {
    results,
    selectedIndex,
    handleSelect,
    handleKeyNavigation
  }
}
